/**
 * Dashboard collectoren section (Fase 5).
 *
 * A lean collectoren overview for the project dashboard: groups the (already
 * fase-filtered) collectors by blok → verdieping, renders expandable rows with
 * an inline edit form (incl. kringen), a "Nieuwe Collector" create form, and a
 * "nog niet uitgevoerd" badge for collectors that are not yet op druk.
 *
 * Deliberately does NOT embed the 3077-line CollectorenTab — the row, edit form
 * and create form are replicated in sibling files (Igor accepted the
 * duplication). The collectors passed in are pre-filtered by the parent
 * (DashboardTab) — this section only groups + renders them, never re-fetches or
 * re-filters by fase.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Collector, PhotoItem } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { listProjectPhotoBatch, generateProjectPdf, createFotoRapportShareLink } from '@/api/tasks';
import { buildBlokGroups, formatShortDate } from '@/utils/collectors';
import { findKringGaps, formatKringGaps } from '@/utils/kringGaps';
import { DashboardCollectorEditForm } from './DashboardCollectorEditForm';
import { DashboardCollectorCreateForm } from './DashboardCollectorCreateForm';
import { CadJsonPicker } from '@/components/cad-import/CadJsonPicker';
import { CadCollectorImportWizard } from '@/components/cad-import/CadCollectorImportWizard';
import type { CadJsonPayload } from '@/api/cadJson';
import { CollectorPhotoPanel } from './collectoren/CollectorPhotoPanel';
import { DrukattestRow } from './collectoren/DrukattestRow';
import { CollectorUitvoeringEditor } from './collectoren/CollectorUitvoeringEditor';
import { collectorPhotoKey, drukattestPhotosFromBatch } from './collectoren/photoBatchKeys';

interface DashboardCollectorenSectionProps {
  projectId: number;
  projectName: string;
  /** Collectors for the active fase — ALREADY fase-filtered by the parent. */
  collectors: Collector[];
  /** Installer id → name map for the Uitvoering editor (op-druk installateur). */
  installerNames?: Record<string, string>;
  /** True while the collector store slice is still loading for this project. */
  loading?: boolean;
  /** Collector-fetch error message, if any. */
  error?: string | null;
  /** Called after a create/edit/delete so the parent can refresh the fase mapping. */
  onChanged?: () => void;
  /**
   * Werf-level "Collectoren geleverd" toggle. Passed to the photo panel so the Goedkeuren
   * modal can say the collector will be booked at the minprijs (the server decides for real).
   */
  collectorenGeleverd?: boolean;
  /**
   * Dutch scope label for the count copy + empty state, e.g. "deze fase"
   * (default) or "dit project" in the Alles roll-up (Fase 6c).
   */
  faseLabel?: string;
  /** Rendered inside a DashboardSection shell: drop the own card chrome + the
   *  duplicate "Collectoren" heading (the shell supplies both). */
  embedded?: boolean;
}

/**
 * Foto-count chip for the collectoren table.
 *
 * `count === null` means the photo batch is still loading (or failed without a
 * mutation override) — show a neutral unknown dash rather than a misleading "0".
 * A real `0` shows "geen foto's"; a positive count shows the camera + number,
 * coloured by the collector's foto-status.
 */
function FotoCountChip({ collector, count }: { collector: Collector; count: number | null }) {
  if (count === null) {
    return <span className="text-td-muted" aria-label="Foto's laden">—</span>;
  }
  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded-full bg-amber-100 text-amber-800">
        geen foto&apos;s
      </span>
    );
  }
  const status = collector.x_studio_fotos_uitvoering;
  const color =
    status === 'Fotos_Goedgekeurd'
      ? 'bg-td-green/10 text-td-green'
      : status === 'Fotos_Geupload'
        ? 'bg-td-blue/10 text-td-blue'
        : 'bg-gray-100 text-td-muted';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded-full ${color}`}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 1H8.828a2 2 0 00-1.414.586L6.293 2.707A1 1 0 015.586 3H4zm6 11a4 4 0 100-8 4 4 0 000 8z" />
      </svg>
      {count}
    </span>
  );
}

/**
 * Accordion panel with a real <button aria-expanded aria-controls> toggle.
 */
function RowAccordion({
  id,
  title,
  badge,
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  badge?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50/50"
      >
        <span className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-td-navy">
          <svg
            className={`w-3 h-3 text-td-muted transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {title}
        </span>
        {badge}
      </button>
      {open && (
        <div id={id} className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}

function CollectorRow({
  collector,
  isExpanded,
  onToggle,
  canEdit,
  photoCount,
  installerNames,
  uitvoering,
  editForm,
  photoPanel,
  selectable = false,
  selected = false,
  onToggleSelect,
}: {
  collector: Collector;
  isExpanded: boolean;
  onToggle: () => void;
  canEdit: boolean;
  photoCount: number | null;
  installerNames?: Record<string, string>;
  uitvoering: React.ReactNode;
  editForm: React.ReactNode;
  photoPanel: React.ReactNode;
  /** Render the leading selection checkbox (foto-rapport selection). */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  const opDruk = !!collector.x_studio_collector_op_druk;
  const installerId = collector.x_studio_collector_installateur?.[0];
  const installerName = installerId ? (installerNames?.[String(installerId)] ?? null) : null;
  // Gap in the kring sequence → AutoCAD extraction dropped a kring. Flag it loud.
  const kringGaps = findKringGaps((collector.kringen ?? []).map((k) => k.x_kringnummer));

  // Per-row accordion state: Foto's open by default, Bewerken collapsed.
  const [fotoOpen, setFotoOpen] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  return (
    <Fragment>
      <tr
        className={`border-t border-gray-100 hover:bg-gray-50/50 cursor-pointer ${isExpanded ? 'bg-gray-50/50' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`collector-detail-${collector.id}`}
        aria-label={`${collector.name} — details ${isExpanded ? 'verbergen' : 'tonen'}`}
        onClick={onToggle}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        {selectable && (
          <td className="pl-4 pr-1 py-2 w-9" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect?.()}
              aria-label={`${collector.name} selecteren voor foto rapport`}
              className="h-4 w-4 rounded border-gray-300 text-td-blue focus:ring-td-blue cursor-pointer"
            />
          </td>
        )}
        <td className="px-4 py-2 text-td-navy">
          <div className="flex items-center gap-2 min-w-0">
            <svg
              className={`w-3 h-3 text-td-muted shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="truncate">{collector.name}</span>
            {!collector.materiaallijst_id && (
              <span className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800">
                {'⚠'} Materiaallijst ontbreekt
              </span>
            )}
            {!opDruk && (
              <span className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800">
                {'⚠'} nog niet uitgevoerd
              </span>
            )}
            {kringGaps.length > 0 && (
              <span
                className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-700"
                title={`${formatKringGaps(kringGaps)} — controleer de CAD-extractie`}
              >
                {'⚠'} {formatKringGaps(kringGaps)}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-2 text-td-navy text-right whitespace-nowrap">
          {collector.x_studio_collector_m2}
        </td>
        <td className="px-4 py-2 text-td-navy whitespace-nowrap hidden sm:table-cell">
          {collector.x_studio_collector_systeem || '-'}
          {collector.materiaallijstMismatch && (
            <span
              className="ml-1.5 inline-flex items-center gap-0.5 text-xs font-bold text-orange-600 cursor-help"
              title={`Systeem komt niet overeen met de materiaallijst${
                collector.materiaallijstSystems?.length
                  ? ` (lijst bevat: ${collector.materiaallijstSystems.join(', ')})`
                  : ''
              }`}
            >
              {'⚠'}
            </span>
          )}
        </td>
        <td className="px-4 py-2 text-center">
          {opDruk ? (
            <div className="flex flex-col items-center gap-0.5">
              <span className="inline-flex items-center gap-1 text-sm font-bold text-td-green whitespace-nowrap">
                {'✓'}
                {collector.x_studio_collector_op_druk_datum && (
                  <span className="text-xs ml-1 font-normal text-td-muted">
                    {formatShortDate(collector.x_studio_collector_op_druk_datum)}
                  </span>
                )}
              </span>
              {installerName && (
                <span className="text-[10px] text-td-muted truncate max-w-[9rem]">{installerName}</span>
              )}
            </div>
          ) : (
            <span className="text-sm font-bold text-gray-400">{'✗'}</span>
          )}
        </td>
        <td className="px-4 py-2 hidden sm:table-cell">
          <FotoCountChip collector={collector} count={photoCount} />
        </td>
      </tr>
      {isExpanded && (
        <tr id={`collector-detail-${collector.id}`}>
          <td colSpan={selectable ? 6 : 5} className="px-2 py-2">
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              {uitvoering}
              {canEdit && (
                <RowAccordion
                  id={`edit-${collector.id}`}
                  title="Collector bewerken"
                  open={editOpen}
                  onToggle={() => setEditOpen((v) => !v)}
                >
                  {editForm}
                </RowAccordion>
              )}
              {photoPanel && (
                <RowAccordion
                  id={`fotos-${collector.id}`}
                  title="Foto's"
                  badge={
                    photoCount !== null ? (
                      <span className="text-[11px] font-bold text-td-muted normal-case">
                        {photoCount} {photoCount === 1 ? 'foto' : "foto's"}
                      </span>
                    ) : undefined
                  }
                  open={fotoOpen}
                  onToggle={() => setFotoOpen((v) => !v)}
                >
                  {photoPanel}
                </RowAccordion>
              )}
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

/** One shareable link with copy/open — the rapport and its loose-photo ZIP each get one. */
function ShareLinkRow({ label, url, ariaLabel }: { label: string; url: string; ariaLabel: string }) {
  return (
    <div className="mt-2 flex items-center gap-2 rounded-md bg-white border border-gray-200 px-2.5 py-1.5">
      <span className="shrink-0 text-xs font-semibold text-td-muted">{label}</span>
      <input
        type="text"
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
        className="flex-1 min-w-0 bg-transparent text-xs text-td-navy outline-none"
        aria-label={ariaLabel}
      />
      <button
        type="button"
        onClick={() => navigator.clipboard?.writeText(url)}
        className="shrink-0 text-xs font-semibold text-td-blue hover:underline"
      >
        Kopieer
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 text-xs font-semibold text-td-blue hover:underline"
      >
        Open
      </a>
    </div>
  );
}

export function DashboardCollectorenSection({ projectId, projectName, collectors, installerNames = {}, loading = false, error = null, onChanged, faseLabel = 'deze fase', embedded = false, collectorenGeleverd = false,
}: DashboardCollectorenSectionProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Foto-rapport selection: off by default. The user opts in via the "Foto Rapport
  // maken" header button (rapportMode), which reveals the per-collector checkboxes
  // and the action bar. selectedIds holds the picked collector ids for the subset
  // report. Both reset on project change so nothing bleeds across projects.
  const [rapportMode, setRapportMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [sharingRapport, setSharingRapport] = useState(false);
  const [rapportError, setRapportError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [zipShareUrl, setZipShareUrl] = useState<string | null>(null);
  useEffect(() => {
    setRapportMode(false);
    setSelectedIds(new Set());
    setShareUrl(null);
    setZipShareUrl(null);
    setRapportError(null);
  }, [projectId]);
  // Exit foto-rapport mode: clear the selection + any generated link/error.
  const exitRapportMode = () => {
    setRapportMode(false);
    setSelectedIds(new Set());
    setShareUrl(null);
    setZipShareUrl(null);
    setRapportError(null);
  };

  // Photo batch: one listProjectPhotoBatch call feeds both the per-collector panels
  // and the per-blok drukattest strips (avoids N+1 photo fetches). `undefined` =
  // still loading → count chip shows "unknown", panels self-fetch as a fallback.
  const [batchPhotos, setBatchPhotos] = useState<Record<string, PhotoItem[]> | undefined>(undefined);
  // Bumped whenever a collector is created, edited, split or deleted — a rename
  // changes the photo prefix, so the batch above has to be re-fetched.
  const [photoReloadKey, setPhotoReloadKey] = useState(0);
  // Per-collector live count override, fed by CollectorPhotoPanel's onPhotosChanged
  // after upload/delete/move, so the count chip never reads a stale batch.
  const [countOverride, setCountOverride] = useState<Record<number, number>>({});
  // Bumped when a photo is moved to drukattest, so the per-blok strip refetches.
  const [drukRefresh, setDrukRefresh] = useState(0);
  // Collector CAD-JSON bulk import (parity with CollectorenTab). The standalone
  // CadJsonPicker → CadCollectorImportWizard flow. Two close paths so we only pay
  // the fase-mapping refetch when it's actually needed:
  //  - picker cancel (no file chosen) → just reset, NO onChanged.
  //  - wizard close (after an import; the wizard writes to Odoo + refetches the
  //    collector store itself) → reset AND fire onChanged so the dashboard re-runs
  //    fetchProjectFases and re-groups the freshly imported collectors into fases.
  const [cadPickerOpen, setCadPickerOpen] = useState(false);
  const [cadPayload, setCadPayload] = useState<CadJsonPayload | null>(null);
  const closeCadPicker = () => {
    setCadPickerOpen(false);
    setCadPayload(null);
  };
  const closeCadWizard = () => {
    setCadPickerOpen(false);
    setCadPayload(null);
    onChanged?.();
  };

  // DashboardTab does NOT remount on a projectId change (ProjectView keeps it
  // mounted; only props change). Local UI state would otherwise leak across the
  // switch — most dangerously a still-open CAD wizard holding project A's payload
  // while createCollector now writes with project B's id. Reset on projectId change.
  // Deliberately NOT firing onChanged here: the new project's fase mapping is
  // fetched by DashboardTab's own projectId effect, not by us.
  useEffect(() => {
    setCadPickerOpen(false);
    setCadPayload(null);
    setExpandedId(null);
    setShowCreateForm(false);
    setCountOverride({});
    setDrukRefresh(0);
  }, [projectId]);

  // Photo VIEW gate (batch prefetch + per-collector/-blok panels, all `photos:read`).
  // The photo panels self-fetch on mount, so when a role lacks photos:read we must
  // NOT render them at all (rendering would 403-spam). oplijster holds photos:read.
  const canViewPhotos = usePermissionsStore((s) => s.dashboardCap)('photosRead');

  // Any collector mutation can move a photo prefix (a rename, a split), so refresh
  // the batch alongside whatever the parent does with its fase mapping.
  const handleCollectorChanged = useCallback(() => {
    setPhotoReloadKey((k) => k + 1);
    onChanged?.();
  }, [onChanged]);

  // Prefetch the whole project photo batch. Keyed on projectId AND projectName so
  // the S3 prefixes can never go stale (the section does not remount on switch),
  // and on `photoReloadKey` so a rename re-keys it: the batch is a map from photo
  // PREFIX to photos, and a prefix carries the collector NAME, so after a rename or
  // a split every renamed card would look itself up under its old prefix and report
  // "geen foto's" until the page was reloaded.
  // Silent failure → batchPhotos stays undefined and panels self-fetch.
  useEffect(() => {
    let cancelled = false;
    setBatchPhotos(undefined);
    // Skip the photo batch entirely for roles without photos:read — the endpoint
    // 403s and the panels are not rendered anyway (see canViewPhotos below).
    if (!canViewPhotos) return;
    listProjectPhotoBatch(projectName)
      .then((data) => { if (!cancelled) setBatchPhotos(data); })
      .catch(() => { /* per-panel self-fetch fallback */ });
    return () => { cancelled = true; };
  }, [projectId, projectName, canViewPhotos, photoReloadKey]);

  // Collectors WITHOUT a materiaallijst link can't be reliably mapped to a fase —
  // resolveCollectorFases falls back to date-proximity, so their fase here is a
  // guess. Surface that so the indeling is never silently misleading.
  // NOTE: this is a LOWER BOUND — a collector that HAS a materiaallijst whose
  // picking lacks an x_studio_projecttaak is also date-guessed server-side, but the
  // frontend can't see that here (it only knows materiaallijst_id). Exposing true
  // per-collector certainty would need the /fases response to flag it.
  const uncertainCount = collectors.filter((c) => !c.materiaallijst_id).length;

  // Mirror CollectorenTab's gate: create/edit/delete require collector_edit.
  // Today the dashboard tab is admin-only (admin holds collector_edit), so this
  // is a no-op; it prevents over-exposure when the tab is later granted to a
  // role without write rights (e.g. projectleider).
  const canEdit = usePermissionsStore((s) => s.canFeature)('collector_edit');
  // Mirror CollectorenTab: the op-druk / installateur / datum edit is gated by
  // collector_toggle (separate from collector_edit).
  const canToggleOpDruk = usePermissionsStore((s) => s.canFeature)('collector_toggle');
  // Foto rapport (download + share) — same gate as CollectorenTab's buttons.
  const canRapport = usePermissionsStore((s) => s.canFeature)('photo_approve');

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Select-all toggles over the currently-visible (fase-filtered) collectors.
  const visibleIds = useMemo(() => collectors.map((c) => c.id), [collectors]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });

  const handleGeneratePdf = async () => {
    if (selectedIds.size === 0 || generatingPdf) return;
    setGeneratingPdf(true);
    setRapportError(null);
    try {
      await generateProjectPdf(projectId, [...selectedIds]);
    } catch (err) {
      setRapportError(err instanceof Error ? err.message : 'PDF genereren mislukt');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleShareRapport = async () => {
    if (selectedIds.size === 0 || sharingRapport) return;
    setSharingRapport(true);
    setRapportError(null);
    setShareUrl(null);
    setZipShareUrl(null);
    try {
      const link = await createFotoRapportShareLink(projectId, [...selectedIds]);
      setShareUrl(link.url);
      setZipShareUrl(link.zipUrl ?? null);
    } catch (err) {
      setRapportError(err instanceof Error ? err.message : 'Deellink genereren mislukt');
    } finally {
      setSharingRapport(false);
    }
  };

  // Read the full project collector set + pickings from the store for the
  // autocomplete datalists / materiaallijst picker (the prop list is fase-scoped,
  // so blok/verdieping suggestions should still reflect the whole project).
  const { allCollectors, pickings, pickingsLoading } = useProjectViewStore(
    useShallow((s) => ({
      allCollectors: s.collectors,
      pickings: s.collectorPickings,
      pickingsLoading: s.collectorLoading,
    })),
  );

  const existingBloks = useMemo(() => {
    const bloks = new Set<string>();
    for (const c of allCollectors ?? []) {
      if (c.x_studio_blok && c.x_studio_blok[1]) bloks.add(c.x_studio_blok[1]);
    }
    return Array.from(bloks).sort();
  }, [allCollectors]);

  const existingVerdiepingen = useMemo(() => {
    const verd = new Set<string>();
    for (const c of allCollectors ?? []) {
      if (c.x_studio_verdieping) verd.add(c.x_studio_verdieping);
    }
    return Array.from(verd).sort();
  }, [allCollectors]);

  const blokGroups = useMemo(() => buildBlokGroups(collectors), [collectors]);
  // Stable per-blok arrays: DrukattestRow re-seeds from prefetchedPhotos whenever its identity changes.
  const drukattestBatch = useMemo(() => {
    if (!batchPhotos) return undefined;
    const byBlok: Record<string, PhotoItem[]> = {};
    for (const { blok } of blokGroups) byBlok[blok] = drukattestPhotosFromBatch(batchPhotos, projectName, blok) ?? [];
    return byBlok;
  }, [batchPhotos, blokGroups, projectName]);
  const pickingList = pickings ?? [];
  const suggestedName = `Collector ${(allCollectors?.length ?? collectors.length) + 1}`;

  const handleToggle = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper className={embedded ? '' : 'rounded-xl border border-gray-200 bg-white p-4 sm:p-5'} aria-label="Collectoren">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          {!embedded && <h2 className="text-base font-extrabold text-td-navy">Collectoren</h2>}
          <p className="text-xs text-td-muted mt-0.5">
            {collectors.length} {collectors.length === 1 ? 'collector' : 'collectoren'} in {faseLabel}
          </p>
        </div>
        {(canEdit || canRapport) && !showCreateForm && (
          <div className="flex items-center gap-2">
            {canRapport && collectors.length > 0 && (
              <button
                type="button"
                onClick={() => (rapportMode ? exitRapportMode() : setRapportMode(true))}
                aria-pressed={rapportMode}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition ${
                  rapportMode
                    ? 'bg-td-blue text-white hover:bg-td-blue/90'
                    : 'border border-td-blue text-td-blue hover:bg-blue-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {rapportMode ? 'Annuleren' : 'Foto Rapport maken'}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => setCadPickerOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-td-green text-td-green rounded-lg hover:bg-green-50 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Importeer vanuit CAD
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-td-blue text-white rounded-lg hover:bg-td-blue/90 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nieuwe Collector
              </button>
            )}
          </div>
        )}
      </div>

      {rapportMode && collectors.length > 0 && (
        <div className="mb-4 rounded-lg border border-td-blue/30 bg-blue-50/50 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-semibold text-td-blue hover:underline"
            >
              {allVisibleSelected ? 'Selectie wissen' : 'Selecteer alles'}
            </button>
            <span className="text-xs text-td-muted">
              {selectedIds.size === 0
                ? 'Selecteer collectoren voor een foto rapport'
                : `${selectedIds.size} ${selectedIds.size === 1 ? 'collector' : 'collectoren'} geselecteerd`}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={handleGeneratePdf}
                disabled={selectedIds.size === 0 || generatingPdf}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-td-blue text-td-blue rounded-lg hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {generatingPdf ? 'PDF genereren…' : 'Foto Rapport'}
              </button>
              <button
                type="button"
                onClick={handleShareRapport}
                disabled={selectedIds.size === 0 || sharingRapport}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-td-blue text-td-blue rounded-lg hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                title="Genereer een deelbare link naar het foto rapport"
              >
                {sharingRapport ? 'Link genereren…' : 'Deel Rapport'}
              </button>
            </div>
          </div>
          {rapportError && (
            <p className="mt-2 text-xs text-td-magenta">{rapportError}</p>
          )}
          {shareUrl && (
            <>
              <ShareLinkRow label="Foto rapport (PDF)" url={shareUrl} ariaLabel="Deellink foto rapport" />
              {zipShareUrl ? (
                <ShareLinkRow label="Losse foto's (ZIP)" url={zipShareUrl} ariaLabel="Deellink losse foto's" />
              ) : (
                <p className="mt-1 text-xs text-td-muted">Geen losse foto's (ZIP): er zijn geen foto's gevonden of de ZIP kon niet gemaakt worden.</p>
              )}
            </>
          )}
        </div>
      )}

      {uncertainCount > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          <span aria-hidden="true">⚠</span>
          <span>
            {'⚠ '}
            {uncertainCount} {uncertainCount === 1 ? 'collector' : 'collector(en)'} zonder materiaallijst — koppel ze
            zodat de onderaannemer correct wordt gefactureerd. (fase-indeling is bovendien geschat op datum totdat je
            koppelt.)
          </span>
        </div>
      )}

      {canEdit && showCreateForm && (
        <div className="mb-5">
          <DashboardCollectorCreateForm
            projectId={projectId}
            existingBloks={existingBloks}
            existingVerdiepingen={existingVerdiepingen}
            pickings={pickingList}
            initialName={suggestedName}
            onClose={() => setShowCreateForm(false)}
            onCreated={handleCollectorChanged}
          />
        </div>
      )}

      {/* Collector CAD-JSON import (parity with CollectorenTab). Standalone components. */}
      {canEdit && cadPickerOpen && !cadPayload && (
        <CadJsonPicker
          projectId={projectId}
          title="CAD JSON kiezen voor collectoren"
          onSelect={(payload) => setCadPayload(payload)}
          onClose={closeCadPicker}
        />
      )}
      {canEdit && cadPayload && (
        <CadCollectorImportWizard
          projectId={projectId}
          payload={cadPayload}
          collectors={allCollectors ?? []}
          pickings={pickings}
          pickingsLoading={pickingsLoading}
          existingBloks={existingBloks}
          existingVerdiepingen={existingVerdiepingen}
          onClose={closeCadWizard}
        />
      )}

      {collectors.length === 0 ? (
        error ? (
          <p className="text-sm text-td-magenta py-6 text-center">Collectoren laden mislukt: {error}</p>
        ) : loading ? (
          <p className="text-sm text-td-muted py-6 text-center">Collectoren laden…</p>
        ) : (
          <p className="text-sm text-td-muted py-6 text-center">Geen collectoren in {faseLabel}.</p>
        )
      ) : (
        <div className="space-y-5">
          {blokGroups.map((blokGroup) => (
            <div key={blokGroup.blok}>
              <h3 className="text-sm font-bold text-td-navy mb-2">
                {blokGroup.blok}
                <span className="ml-2 text-xs font-normal text-td-muted">
                  {blokGroup.totalCollectors} {blokGroup.totalCollectors === 1 ? 'collector' : 'collectoren'}
                </span>
              </h3>
              {/* Drukattest strip — per blok, at the top (parity with CollectorenTab).
                  Self-fetches `photos:read`, so only mount it when the role may view photos. */}
              {canViewPhotos && (
                <div className="mb-3">
                  <DrukattestRow
                    projectId={projectId}
                    projectName={projectName}
                    blokName={blokGroup.blok}
                    refreshKey={drukRefresh}
                    prefetchedPhotos={drukattestBatch?.[blokGroup.blok]}
                  />
                </div>
              )}
              <div className="space-y-3">
                {blokGroup.verdiepingen.map((verdiepingGroup) => (
                  <div key={`${blokGroup.blok}-${verdiepingGroup.verdieping}`}>
                    <p className="text-[11px] font-semibold text-td-muted uppercase tracking-wide mb-1">
                      {verdiepingGroup.verdieping}
                    </p>
                    <div className="rounded-xl border border-gray-200 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            {rapportMode && <th scope="col" className="w-9 px-2 py-2.5"><span className="sr-only">Selecteren</span></th>}
                            <th scope="col" className="text-left px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">Naam</th>
                            <th scope="col" className="text-right px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">m&sup2;</th>
                            <th scope="col" className="text-left px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide hidden sm:table-cell">Systeem</th>
                            <th scope="col" className="text-center px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">Op druk</th>
                            <th scope="col" className="text-left px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide hidden sm:table-cell">Foto&apos;s</th>
                          </tr>
                        </thead>
                        <tbody>
                          {verdiepingGroup.collectors.map((collector) => {
                            const collectorKey = collectorPhotoKey(collector, projectName);
                            const batchCount = batchPhotos ? (batchPhotos[collectorKey]?.length ?? 0) : null;
                            const photoCount = countOverride[collector.id] ?? batchCount;
                            return (
                              <CollectorRow
                                key={collector.id}
                                collector={collector}
                                isExpanded={expandedId === collector.id}
                                onToggle={() => handleToggle(collector.id)}
                                canEdit={canEdit}
                                photoCount={photoCount}
                                installerNames={installerNames}
                                selectable={rapportMode}
                                selected={selectedIds.has(collector.id)}
                                onToggleSelect={() => toggleSelect(collector.id)}
                                uitvoering={
                                  canToggleOpDruk ? (
                                    <CollectorUitvoeringEditor
                                      collector={collector}
                                      projectId={projectId}
                                      installerNames={installerNames}
                                    />
                                  ) : null
                                }
                                editForm={
                                  <DashboardCollectorEditForm
                                    collector={collector}
                                    projectId={projectId}
                                    existingBloks={existingBloks}
                                    existingVerdiepingen={existingVerdiepingen}
                                    pickings={pickingList}
                                    installerNames={installerNames}
                                    onClose={() => setExpandedId(null)}
                                    onChanged={handleCollectorChanged}
                                  />
                                }
                                photoPanel={
                                  canViewPhotos ? (
                                    <CollectorPhotoPanel
                                      collector={collector}
                                      projectId={projectId}
                                      projectName={projectName}
                                      prefetchedPhotos={batchPhotos ? batchPhotos[collectorKey] : undefined}
                                      installateur={(() => {
                                        const installId = collector.x_studio_collector_installateur?.[0];
                                        if (!installId) return null;
                                        const name = installerNames?.[String(installId)];
                                        return { id: installId, name: name ?? `Installateur ${installId}` };
                                      })()}
                                      collectorenGeleverd={collectorenGeleverd}
                                      onMovedToDrukattest={() => setDrukRefresh((k) => k + 1)}
                                      onPhotosChanged={(n) =>
                                        setCountOverride((prev) => (prev[collector.id] === n ? prev : { ...prev, [collector.id]: n }))
                                      }
                                    />
                                  ) : null
                                }
                              />
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Wrapper>
  );
}
